// src/components/FAQSection.jsx   (or place it wherever suitable)

import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const faqData = {
  english: [
    {
      question: 'WHAT SERVICES DO YOU OFFER?',
      answer:
        'We specialize in visual content for the hospitality industry, from photography and video to social content, branding, and campaigns. Everything you need to make your brand truly irresistible.',
    },
    {
      question: 'DO YOU ONLY WORK WITH HOSPITALITY BUSINESSES?',
      answer:
        'Hospitality is our first love. However, if you have a strong passion for atmosphere, experience, and great design, chances are we’ll be a great match even outside the hospitality sector.',
    },
    {
      question: 'WHAT DOES IT COST TO WORK WITH YOU?',
      answer:
        'Pricing depends on your needs and goals. We work with transparent packages and always think along within your budget. Feel free to request a price indication.',
    },
    {
      question: 'HOW QUICKLY CAN YOU GET STARTED?',
      answer:
        'We usually move fast. We’ll schedule a kick-off together and assess what’s possible in the short term.',
    },
    {
      question: 'CAN YOU ALSO CREATE A FULL CONTENT STRATEGY?',
      answer:
        'Absolutely. From strategy to execution, we help you build a plan that works and looks good enough to savor.',
    },
  ],
  dutch: [
    {
      question: 'WAT VOOR DIENSTEN BIEDEN JULLIE PRECIES AAN?',
      answer:
        'We zijn gespecialiseerd in visuele content voor horeca. van fotografie en video tot social content, branding en campagnes. Alles om jouw merk onweerstaanbaar te maken.',
    },
    {
      question: 'WERKEN JULLIE ALLEEN MET HORECAZAKEN?',
      answer:
        'Horeca is onze eerste liefde, maar als jij passie hebt voor sfeer, beleving en goed design, dan matchen we sowieso goed.',
    },
    {
      question: 'WAT KOST HET OM MET JULLIE TE WERKEN?',
      answer:
        'Dat hangt af van je wensen, maar we werken met transparante pakketten en denken altijd mee binnen je budget. Vraag gerust een prijsindicatie aan.',
    },
    {
      question: 'HOE SNEL KUNNEN JULLIE AAN DE SLAG?',
      answer:
        'Meestal kunnen we snel schakelen! We plannen samen een kick-off en kijken wat er mogelijk is op korte termijn.',
    },
    {
      question: 'KUNNEN JULLIE OOK EEN VOLLEDIGE CONTENTSTRATEGIE MAKEN?',
      answer:
        'Zeker! Van strategie tot uitvoering: we helpen je met een plan dat werkt én eruit ziet om op te eten.',
    },
  ],
};

export default function FAQSection() {
  const [language, setLanguage] = useState('english'); // 'english' or 'dutch'
  const [expanded, setExpanded] = useState(false);     // only one panel open at a time

  const handleChangeLanguage = (event, newValue) => {
    setLanguage(newValue);
    setExpanded(false); // reset expanded state when language changes
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const currentFAQs = faqData[language];

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'white',
        mx: 'auto',
      }}
    >
      {/* Header with title and tabs */}
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          color="text.primary"
          sx={{ letterSpacing: 0.5 }}
        >
          FREQUENTLY ASKED QUESTIONS
        </Typography>

        <Tabs
          value={language}
          onChange={handleChangeLanguage}
          aria-label="language selection"
          sx={{
            minHeight: 36,
            '& .MuiTabs-indicator': {
              backgroundColor: '#ff2c6d',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '12px',
              minHeight: 36,
              px: 3,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: '#ff2c6d',
              },
            },
          }}
        >
          <Tab label="Dutch" value="dutch" />
          <Tab label="English" value="english" />
        </Tabs>
      </Box>

      {/* FAQ Accordion List */}
      <Box sx={{ px: { xs: 2, md: 5 }, py: 2,   }}>
        {currentFAQs.map((item, index) => {
          const panelId = `panel${index}`;

          return (
            <Accordion
              key={index}
              expanded={expanded === panelId}
              onChange={handleAccordionChange(panelId)}
              disableGutters
              elevation={0}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                borderRadius: 0,
                '&:before': { display: 'none' },
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{
                  py: 1.5,
                  px: 0,
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.primary"
                >
                  {`${index + 1}. ${item.question}`}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 0, pt: 0, pb: 3 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  lineHeight={1.7}
                >
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Paper>
  );
}